/**
 * AddQualificationDialog Component
 * Dialog for adding hidden qualifications back to the matrix view
 */

import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Shield, Plus } from 'lucide-react';

const AddQualificationDialog = memo(({
  open,
  onOpenChange,
  availableQualifications,
  onAddQualification,
  onCreateNew
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Qualification to Matrix</DialogTitle>
          <DialogDescription>
            Select qualifications to add to the matrix view. These qualifications exist but are hidden from the matrix.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {availableQualifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>All qualifications are already on the matrix</p>
            </div>
          ) : (
            availableQualifications.map((qual) => (
              <div
                key={qual.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{qual.name}</p>
                  {qual.description && (
                    <p className="text-xs text-gray-500 mt-1">{qual.description}</p>
                  )}
                  {qual.category && (
                    <Badge variant="outline" className="mt-1 text-xs">
                      {qual.category}
                    </Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => onAddQualification(qual.id)}
                  className="bg-blue-600 hover:bg-blue-700 ml-3"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            ))
          )}
        </div>

        {/* Add New Qualification Button - Centered Below List */}
        <div className="flex justify-center pt-4 pb-2">
          <Button
            variant="secondary"
            onClick={onCreateNew}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Qualification
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

AddQualificationDialog.displayName = 'AddQualificationDialog';

export default AddQualificationDialog;
